import * as React from 'react';
import { getLocalization } from 'global/global';
import { useAppSelector } from 'index';
import { NavItem } from 'Interfaces/Admin';
import {
  adminGroupsSelector, adminUsersSelector, collectDescendants, collectGroupUsers,
} from 'reducers/admin/selectors';

interface Props {
  item: NavItem;
  /** The item the user is looking at right now. */
  selected?: boolean;
  /** On the path to the selection, but not the selection itself. */
  onTrail?: boolean;
  inactive?: boolean;
  /** Clicking the name opens the item for editing. */
  onSelect: () => void;
  /** Clicking the count/chevron opens the item's children in the next column. */
  onDrillDown: () => void;
}

/**
 *  One row of the group/user navigation, shared by the sidebar and the columns.
 *  Replaces the #itemTemplate and #panelItemTemplate underscore templates.
 */
export default function NavListItem(props: Props) {
  const { item, selected, onTrail, inactive } = props;
  const groups = useAppSelector(adminGroupsSelector);
  const users = useAppSelector(adminUsersSelector);

  const group = item.type === 'group' ? groups.find((g) => g.id === item.id) : undefined;

  /**
   *  Subgroups are counted regardless of their active flag while users are
   *  counted only when active — an asymmetry carried over from LeftView.js and
   *  PanelView.js, which called getSubgroups() without the flag.
   */
  const counts = group
    ? `(${collectDescendants(groups, item.id).length}/${collectGroupUsers(users, groups, item.id, true, true).length})`
    : '';

  const isOrganization = group?.organization === true && group?.superuser_group === false;
  const isSuperUser = group?.superuser_group === true;
  const hasChildren = item.children.length > 0;

  const classes = [
    'list-group-item',
    selected ? 'active' : '',
    onTrail ? 'selectionTrack' : '',
  ].filter(Boolean).join(' ');

  return (
    <a
      href="#"
      className={classes}
      onClick={(e) => e.preventDefault()}
    >
      <div className="row listItem">
        {isOrganization && (
          <div className="col-1 nopadding">
            <span className="org-icon" aria-hidden="true" title={getLocalization('groupOrganization')} />
          </div>
        )}
        {isSuperUser && (
          <div className="col-2 nopadding">
            <span className="superuser-label" aria-hidden="true">{getLocalization('superuser')}</span>
          </div>
        )}
        <div
          className={`col columnName nopadding ${group ? 'bold' : ''} ${inactive ? 'inactive' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            props.onSelect();
          }}
        >
          {item.name}
        </div>
        {hasChildren && (
          <div
            className="col-4 columnDetails arrow nopadding"
            title={getLocalization('arrowHover')}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.onDrillDown();
            }}
          >
            {counts}
            <i className="fa fa-chevron-right" aria-hidden="true" />
          </div>
        )}
      </div>
    </a>
  );
}
